import React, { useState, useEffect } from 'react';
import { Home, Clock, Target, Dumbbell, Award, Flame, BarChart3, Zap, Heart, Star, Calendar, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CHART_COLORS = ['#f97316', '#3b82f6', '#a855f7', '#ec4899', '#ef4444', '#6b7280'];

const ENCOURAGEMENT = {
  0:  ["Every journey starts with a single step. Today is your day! 🌟", "Rest is productive too. When you're ready, you've got this! 💪", "New beginnings are beautiful. Let's make today count! ✨", "Your potential is unlimited. Start small, dream big! 🚀", "Taking it slow? That's okay. Progress over perfection! 🌱"],
  30: ["You're showing up! That's what matters most. Keep going! 🌟", "Small steps lead to big changes. You're on the right track! 🎯", "Building momentum one task at a time. Proud of you! 💫", "Every effort counts. You're doing better than you think! 🌈", "The fact that you started is already a win! Keep it up! 🏆"],
  50: ["You're finding your rhythm! This is great progress! 🎵", "Consistency is key, and you're nailing it! Keep going! 🔥", "Look at you go! Your dedication is inspiring! 💪", "You're building something amazing here. Keep pushing! 🌟", "Solid work! You're stronger than yesterday! 🚀"],
  75: ["Impressive! You're in the zone and crushing it! 🔥", "Your hard work is paying off big time! Amazing! 🏆", "You're on fire! This is the momentum we love to see! ⚡", "Excellence in action! Keep this energy going! 🌟", "You're a productivity powerhouse today! Incredible! 💎"],
  100: ["LEGENDARY! You're absolutely unstoppable today! 🏆🔥", "Peak performance unlocked! You're a superstar! ⭐✨", "Phenomenal work! You're setting the bar high! 🚀💪", "This is what excellence looks like! Outstanding! 👑💎", "You're crushing it at the highest level! Incredible! ⚡🌟"],
};

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const getEncouragingMessage = (score) => {
  if (score >= 75) return pick(ENCOURAGEMENT[100]);
  if (score >= 50) return pick(ENCOURAGEMENT[75]);
  if (score >= 30) return pick(ENCOURAGEMENT[50]);
  if (score > 0)   return pick(ENCOURAGEMENT[30]);
  return pick(ENCOURAGEMENT[0]);
};

const getDaysSince = (startDate) => {
  const now = new Date(); now.setHours(0,0,0,0);
  const s   = new Date(startDate); s.setHours(0,0,0,0);
  return Math.ceil(Math.abs(now - s) / 86400000);
};

const getStartDate = (now, range) => {
  const d = new Date(now); d.setHours(0,0,0,0);
  if (range === 'week')  d.setDate(d.getDate() - 7);
  if (range === 'month') d.setMonth(d.getMonth() - 1);
  if (range === 'all')   d.setFullYear(d.getFullYear() - 1);
  return d;
};

const getIntensityColor = (i) => ({ 0:'#f5f5f5', 1:'#fed7aa', 2:'#fb923c', 3:'#f97316', 4:'#c2410c' }[i] || '#f5f5f5');

// ── Chart wrapper ─────────────────────────────────────────────

const ChartBox = ({ children, empty }) => (
  <div className={`h-[200px] sm:h-[250px] border-2 border-neutral-900 p-2 ${empty ? 'flex items-center justify-center' : ''}`}>
    {empty ? <span className="text-neutral-500 font-mono font-bold">NO DATA AVAILABLE</span> : children}
  </div>
);

// ── Stat card (overview grid) ─────────────────────────────────

const StatCard = ({ Icon, iconColor, value, label, sub1, sub2 }) => (
  <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover-lift-xl">
    <div className="flex items-center justify-between mb-2 border-b-2 border-neutral-900 pb-2">
      <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
      <span className="text-2xl sm:text-3xl font-bold text-neutral-900 font-mono">{value}</span>
    </div>
    <div className="text-xs sm:text-sm text-neutral-900 mb-1 font-mono font-bold uppercase">{label}</div>
    {sub1 && <div className="text-xs text-neutral-700 font-mono">{sub1}</div>}
    {sub2 && <div className="text-xs text-neutral-700 font-mono">{sub2}</div>}
  </div>
);

// ── Section heading ───────────────────────────────────────────

const SectionHead = ({ title, meta }) => (
  <div className="flex items-center gap-3 mb-4 border-b-4 border-neutral-900 pb-2">
    <h2 className="text-xl sm:text-2xl font-serif text-neutral-900 uppercase">{title}</h2>
    {meta && <span className="text-sm text-neutral-700 font-mono font-bold">{meta}</span>}
  </div>
);

// ── Stat detail tile ──────────────────────────────────────────

const DetailTile = ({ value, label, sub, bg = 'bg-neutral-50', valueColor = 'text-neutral-900' }) => (
  <div className={`border-2 border-neutral-900 p-3 ${bg}`}>
    <div className="text-xs sm:text-sm text-neutral-900 mb-2 font-mono font-bold uppercase">{label}</div>
    <div className={`text-2xl sm:text-3xl font-bold font-mono ${valueColor}`}>{value}</div>
    {sub && <div className="text-xs text-neutral-700 font-mono">{sub}</div>}
  </div>
);

// ── Statistics ────────────────────────────────────────────────

const Statistics = ({ setCurrentModule }) => {
  const [timeRange,          setTimeRange]          = useState('week');
  const [stats,              setStats]              = useState(null);
  const [heatmapData,        setHeatmapData]        = useState([]);
  const [productivityScore,  setProductivityScore]  = useState(0);
  const [encouragement,      setEncouragement]      = useState('');
  const [dateRange,          setDateRange]          = useState({ start: '', end: '' });
  const [showBreakdown,      setShowBreakdown]      = useState(false);

  useEffect(() => { loadStats(); }, [timeRange]);

  const loadStats = () => {
    const pomodoros       = JSON.parse(localStorage.getItem('pomodoroCompletions') || '[]');
    const habits          = JSON.parse(localStorage.getItem('habits')              || '[]');
    const habitCompletions = JSON.parse(localStorage.getItem('habitCompletions')   || '{}');
    const workouts        = JSON.parse(localStorage.getItem('workoutHistory')      || '[]');
    const assignments     = JSON.parse(localStorage.getItem('scheduleItems')       || '[]').filter(i => i.type === 'assignment');
    const transactions    = JSON.parse(localStorage.getItem('transactions')        || '[]');

    const now = new Date();
    const startDate = getStartDate(now, timeRange);

    setDateRange({
      start: startDate.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }),
      end:   now.toLocaleDateString('en-US',       { month:'short', day:'numeric', year:'numeric' }),
    });

    const fp = pomodoros.filter(p    => new Date(p.timestamp) >= startDate);
    const fw = workouts.filter(w     => new Date(w.startTime) >= startDate);
    const ft = transactions.filter(t => new Date(t.date)      >= startDate);
    const fh = Object.fromEntries(Object.entries(habitCompletions).filter(([k]) => new Date(k.split('_')[0]) >= startDate));

    const pomodoroStats    = calcPomodoroStats(fp);
    const habitStats       = calcHabitStats(habits, habitCompletions, startDate);
    const workoutStats     = calcWorkoutStats(fw);
    const assignmentStats  = calcAssignmentStats(assignments);
    const financeStats     = calcFinanceStats(ft);

    const days  = getDaysSince(startDate) || 1;
    const score = Math.min(Math.round(((fp.length * 10) + (Object.keys(fh).length * 15) + (fw.length * 25)) / (days * 100) * 100), 100);

    setProductivityScore(score);
    setEncouragement(getEncouragingMessage(score));
    setHeatmapData(buildHeatmap(pomodoros, habitCompletions, workouts));
    setStats({
      pomodoro:    pomodoroStats,
      habits:      habitStats,
      workouts:    workoutStats,
      assignments: assignmentStats,
      finance:     financeStats,
      overview: {
        totalPomodoros:      fp.length,
        totalWorkouts:       fw.length,
        habitCompletionRate: habitStats.completionRate,
        assignmentsOnTime:   assignmentStats.onTimeRate,
      },
    });
  };

  const buildHeatmap = (pomodoros, habitCompletions, workouts) => {
    const today = new Date();
    return Array.from({ length: 84 }, (_, i) => {
      const date = new Date(today); date.setDate(date.getDate() - (83 - i)); date.setHours(0,0,0,0);
      const next = new Date(date); next.setDate(next.getDate() + 1);
      const ds   = date.toDateString();

      const p = pomodoros.filter(x => { const d = new Date(x.timestamp); return d >= date && d < next; }).length;
      const h = Object.keys(habitCompletions).filter(k => k.startsWith(ds) && habitCompletions[k]).length;
      const w = workouts.filter(x => { const d = new Date(x.startTime); return d >= date && d < next; }).length;
      const total = p + h * 2 + w * 3;
      const intensity = total >= 15 ? 4 : total >= 10 ? 3 : total >= 5 ? 2 : total > 0 ? 1 : 0;

      return {
        date: date.toISOString().split('T')[0],
        displayDate: date.toLocaleDateString('en-US', { month:'short', day:'numeric' }),
        intensity,
        activities: { pomodoros: p, habits: h, workouts: w, total },
      };
    });
  };

  if (!stats) {
    return (
      <div className="page flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-neutral-400 mx-auto mb-4 border-4 border-neutral-900 p-2" />
          <div className="text-neutral-900 font-mono font-bold uppercase">Loading statistics...</div>
        </div>
      </div>
    );
  }

  const startDate = getStartDate(new Date(), timeRange);
  const days      = getDaysSince(startDate);

  return (
    <div className="page">
      <div className="page-content-lg">
        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /><span>Dashboard</span>
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="page-title">STATISTICS</h1>
            <BarChart3 className="w-6 h-6 text-neutral-900" />
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-700 font-mono mb-4">
            <Calendar className="w-4 h-4" />
            <span className="font-bold uppercase">{dateRange.start} - {dateRange.end}</span>
          </div>

          <div className="flex gap-2 flex-wrap">
            {[['week','7 Days'],['month','30 Days'],['all','1 Year']].map(([r, label]) => (
              <button
                key={r}
                onClick={() => setTimeRange(r)}
                className={`flex-1 sm:flex-none px-4 py-3 text-xs sm:text-sm uppercase tracking-wider font-bold transition-all border-2 border-neutral-900 font-mono ${
                  timeRange === r ? 'bg-neutral-900 text-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]' : 'bg-white text-neutral-900 hover-lift'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Productivity Score */}
        <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white p-6 sm:p-8 mb-6 card-heavy">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div className="flex-1 w-full">
              <div className="flex items-center gap-3 mb-4 border-b-2 border-white pb-3">
                <Zap className="w-6 h-6 sm:w-8 sm:h-8" />
                <h2 className="text-2xl sm:text-3xl font-serif uppercase">Productivity Score</h2>
              </div>
              <div className="text-5xl sm:text-7xl font-bold mb-4 font-mono">{productivityScore}/100</div>
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <Heart className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="text-sm sm:text-xl font-bold">{encouragement}</span>
              </div>

              <button
                onClick={() => setShowBreakdown(!showBreakdown)}
                className="flex items-center gap-2 text-sm mb-3 font-mono font-bold border-2 border-white px-3 py-2 hover:bg-white hover:text-orange-500 transition-colors"
              >
                {showBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <span>{showBreakdown ? 'HIDE' : 'SHOW'} SCORE BREAKDOWN</span>
              </button>

              {showBreakdown && (
                <div className="bg-black bg-opacity-30 p-4 mb-4 text-sm border-2 border-white font-mono">
                  <div className="font-bold mb-2 uppercase">How the score is calculated:</div>
                  <div className="space-y-1 font-bold">
                    <div>• POMODOROS: {stats.overview.totalPomodoros} × 10 pts = {stats.overview.totalPomodoros * 10} pts</div>
                    <div>• HABITS: {stats.habits.performanceData.length} completed × 15 pts</div>
                    <div>• WORKOUTS: {stats.overview.totalWorkouts} × 25 pts = {stats.overview.totalWorkouts * 25} pts</div>
                    <div className="pt-2 border-t-2 border-white mt-2">
                      MAX POSSIBLE: {days * 100} pts (100 pts/day × {days} days)
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white bg-opacity-30 h-4 overflow-hidden border-2 border-white">
                <div className="bg-white h-full transition-all duration-1000" style={{ width: `${productivityScore}%` }} />
              </div>
            </div>
            <div className="text-right hidden sm:block">
              <Star className="w-16 h-16 ml-auto mb-2 border-4 border-white p-2" />
              <div className="text-sm uppercase tracking-wider font-bold font-mono">Keep Shining</div>
            </div>
          </div>
        </div>

        {/* Heatmap */}
        <div className="bg-white border-4 border-neutral-900 p-4 sm:p-8 mb-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4 border-b-4 border-neutral-900 pb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-serif text-neutral-900 mb-1 uppercase">Activity Heatmap</h2>
              <p className="text-xs sm:text-sm text-neutral-700 font-mono font-bold uppercase">Last 12 weeks · Hover for details</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 text-xs text-neutral-900 font-mono font-bold">
              <span className="uppercase">Less</span>
              <div className="flex gap-1">
                {[0,1,2,3,4].map(i => <div key={i} className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-neutral-900" style={{ backgroundColor: getIntensityColor(i) }} />)}
              </div>
              <span className="uppercase">More</span>
            </div>
          </div>

          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="inline-block min-w-full">
              <div className="flex gap-1">
                {Array.from({ length: 12 }, (_, wi) => (
                  <div key={wi} className="flex flex-col gap-1">
                    {[0,1,2,3,4,5,6].map(di => {
                      const d = heatmapData[wi * 7 + di];
                      return (
                        <div key={di} className="relative group">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-neutral-900 cursor-pointer hover:ring-4 hover:ring-orange-500 transition-all" style={{ backgroundColor: d ? getIntensityColor(d.intensity) : '#f5f5f5' }} />
                          {d && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-neutral-900 text-white text-xs border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 font-mono font-bold">
                              <div className="mb-1 uppercase">{d.displayDate}</div>
                              <div>🍅 {d.activities.pomodoros} POMODOROS</div>
                              <div>🎯 {d.activities.habits} HABITS</div>
                              <div>💪 {d.activities.workouts} WORKOUTS</div>
                              <div className="border-t-2 border-white mt-1 pt-1">TOTAL: {d.activities.total}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="flex gap-1 mt-2">
                {Array.from({ length: 12 }, (_, wi) => {
                  const d = heatmapData[wi * 7];
                  return <div key={wi} className="w-4 sm:w-5 text-xs text-neutral-900 text-center font-mono font-bold">{d && wi % 2 === 0 ? new Date(d.date).toLocaleDateString('en-US', { month:'short' }).toUpperCase() : ''}</div>;
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
          <StatCard Icon={Clock}   iconColor="text-orange-500" value={stats.overview.totalPomodoros}      label="Focus Sessions" sub1={`${stats.pomodoro.totalMinutes} mins total`}   sub2={`Avg ${stats.pomodoro.avgPerDay}/day`} />
          <StatCard Icon={Target}  iconColor="text-green-500"  value={`${stats.overview.habitCompletionRate}%`} label="Habit Rate"     sub1="Overall completion" />
          <StatCard Icon={Dumbbell} iconColor="text-red-500"   value={stats.overview.totalWorkouts}       label="Workouts"       sub1={`${stats.workouts.totalMinutes} mins total`}  sub2={`${stats.workouts.avgDuration}min avg`} />
          <StatCard Icon={Award}   iconColor="text-purple-500" value={`${stats.overview.assignmentsOnTime}%`}  label="On-Time Rate"  sub1={`${stats.assignments.completed} completed`} />
        </div>

        {/* Pomodoro */}
        {stats.pomodoro.total > 0 && (
          <div className="mb-6">
            <SectionHead title="🍅 Focus Sessions" meta={`(${stats.pomodoro.total} sessions, ${stats.pomodoro.totalMinutes} minutes)`} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-2 font-mono uppercase">Daily Activity</h3>
                <p className="text-xs text-neutral-700 mb-4 font-mono">Sessions completed each day</p>
                <ChartBox empty={stats.pomodoro.dailyData.length === 0}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.pomodoro.dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#000" strokeWidth={2} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <YAxis tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="sessions" stroke="#f97316" strokeWidth={3} dot={{ fill:'#f97316', r:4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartBox>
              </div>
              <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-2 font-mono uppercase">Most Productive Hours</h3>
                <p className="text-xs text-neutral-700 mb-4 font-mono">When you focus best</p>
                <ChartBox empty={stats.pomodoro.hourlyData.length === 0}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.pomodoro.hourlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#000" strokeWidth={2} />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <YAxis tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip />
                      <Bar dataKey="sessions" fill="#f97316" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartBox>
                <div className="mt-4 text-xs sm:text-sm text-neutral-900 text-center font-mono font-bold border-2 border-neutral-900 py-2 bg-orange-100">
                  PEAK: {stats.pomodoro.mostProductiveHour}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Habits */}
        {stats.habits.streaks.length > 0 && (
          <div className="mb-6">
            <SectionHead title="🎯 Habits" meta={`(${stats.habits.completionRate}% completion rate)`} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-2 font-mono uppercase">Current Streaks</h3>
                <p className="text-xs text-neutral-700 mb-4 font-mono">Consecutive days completing each habit</p>
                <div className="space-y-4">
                  {stats.habits.streaks.map((streak, idx) => (
                    <div key={idx} className="border-2 border-neutral-900 p-3 bg-neutral-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-neutral-900 truncate pr-2 font-mono font-bold uppercase">{streak.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Flame className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-bold text-neutral-900 font-mono">{streak.currentStreak} DAYS</span>
                        </div>
                      </div>
                      <div className="progress-track">
                        <div className="bg-orange-500 h-full transition-all" style={{ width: `${streak.maxStreak > 0 ? Math.min((streak.currentStreak / streak.maxStreak) * 100, 100) : 0}%` }} />
                      </div>
                      <div className="text-xs text-neutral-700 mt-1 font-mono font-bold">BEST STREAK: {streak.maxStreak} DAYS</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-2 font-mono uppercase">Completion Rate by Habit</h3>
                <p className="text-xs text-neutral-700 mb-4 font-mono">% of days each habit was completed</p>
                <ChartBox empty={stats.habits.performanceData.length === 0}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.habits.performanceData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#000" strokeWidth={2} />
                      <XAxis type="number" domain={[0,100]} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip />
                      <Bar dataKey="rate" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartBox>
              </div>
            </div>
          </div>
        )}

        {/* Workouts */}
        {stats.workouts.total > 0 && (
          <div className="mb-6">
            <SectionHead title="💪 Workouts" meta={`(${stats.workouts.total} sessions, ${stats.workouts.totalMinutes} minutes)`} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-2 font-mono uppercase">Workout Frequency</h3>
                <p className="text-xs text-neutral-700 mb-4 font-mono">Workouts completed each day</p>
                <ChartBox empty={stats.workouts.frequencyData.length === 0}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.workouts.frequencyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#000" strokeWidth={2} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <YAxis tick={{ fontSize: 10, fontWeight: 'bold' }} />
                      <Tooltip />
                      <Bar dataKey="workouts" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartBox>
              </div>
              <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-2 font-mono uppercase">Summary</h3>
                <p className="text-xs text-neutral-700 mb-4 font-mono">Overall workout stats for this period</p>
                <div className="space-y-4 sm:space-y-6">
                  <DetailTile label="Total Volume"     value={stats.workouts.totalVolume.toLocaleString()} sub="lbs lifted (reps × weight)" bg="bg-red-50" />
                  <DetailTile label="Average Duration" value={stats.workouts.avgDuration}                  sub="minutes per workout"         bg="bg-red-50" />
                  <DetailTile label="Total Workouts"   value={stats.workouts.total}                        sub="completed this period"       bg="bg-red-50" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Finance */}
        {stats.finance.categoryData.length > 0 && (
          <div className="mb-6">
            <SectionHead title="💰 Finance" meta={`(Balance: $${stats.finance.balance.toFixed(2)})`} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-2 font-mono uppercase">Spending by Category</h3>
                <p className="text-xs text-neutral-700 mb-4 font-mono">Breakdown of expenses</p>
                <div className="h-[250px] sm:h-[300px] border-2 border-neutral-900 p-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={stats.finance.categoryData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} outerRadius={80} dataKey="value">
                        {stats.finance.categoryData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <h3 className="text-base sm:text-lg font-bold text-neutral-900 mb-2 font-mono uppercase">Financial Summary</h3>
                <p className="text-xs text-neutral-700 mb-4 font-mono">Income, expenses, and balance</p>
                <div className="space-y-4 sm:space-y-6">
                  <DetailTile label="Total Income"   value={`$${stats.finance.income.toFixed(2)}`}   sub="Money earned" bg="bg-green-50"  valueColor="text-green-600" />
                  <DetailTile label="Total Expenses" value={`$${stats.finance.expenses.toFixed(2)}`} sub="Money spent"  bg="bg-red-50"    valueColor="text-red-600" />
                  <DetailTile
                    label="Net Balance"
                    value={`$${stats.finance.balance.toFixed(2)}`}
                    sub="Income minus expenses"
                    bg={stats.finance.balance >= 0 ? 'bg-green-100 border-4' : 'bg-red-100 border-4'}
                    valueColor={stats.finance.balance >= 0 ? 'text-green-600' : 'text-red-600'}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assignments */}
        {stats.assignments.total > 0 && (
          <div className="bg-white border-4 border-neutral-900 p-4 sm:p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] mb-6">
            <div className="flex items-center gap-3 mb-4 border-b-4 border-neutral-900 pb-3">
              <h2 className="text-xl sm:text-2xl font-serif text-neutral-900 uppercase">📚 Assignments</h2>
              <span className="text-sm text-neutral-700 font-mono font-bold">({stats.assignments.total} total)</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              <DetailTile label="Total"       value={stats.assignments.total}          sub="All assignments"  bg="bg-neutral-50"  />
              <DetailTile label="Completed"   value={stats.assignments.completed}      sub="Finished tasks"   bg="bg-green-50"   valueColor="text-green-600" />
              <DetailTile label="Upcoming"    value={stats.assignments.upcoming}       sub="Due soon"         bg="bg-orange-50"  valueColor="text-orange-600" />
              <DetailTile label="On-Time Rate" value={`${stats.assignments.onTimeRate}%`} sub="Completion rate" bg="bg-blue-50" valueColor="text-blue-600" />
            </div>
          </div>
        )}

        {/* Empty state */}
        {stats.pomodoro.total === 0 && stats.habits.streaks.length === 0 && stats.workouts.total === 0 && stats.assignments.total === 0 && (
          <div className="bg-white border-4 border-neutral-900 p-12 text-center shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
            <TrendingUp className="w-16 h-16 text-neutral-400 mx-auto mb-4 border-4 border-neutral-900 p-2" />
            <h3 className="text-xl font-serif text-neutral-900 mb-2 uppercase">No Data Yet</h3>
            <p className="text-neutral-700 mb-6 font-mono font-bold uppercase">Start completing pomodoros, habits, workouts, and assignments to see stats here.</p>
            <button onClick={() => setCurrentModule('dashboard')} className="px-6 py-3 bg-neutral-900 text-white hover:bg-neutral-700 transition-colors border-2 border-neutral-900 font-mono font-bold uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg">
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Stat calculators ──────────────────────────────────────────

const calcPomodoroStats = (pomodoros) => {
  const byDay = {}, byHour = {};
  pomodoros.forEach(p => {
    const day  = new Date(p.timestamp).toLocaleDateString();
    const hour = new Date(p.timestamp).getHours();
    byDay[day]   = (byDay[day]   || 0) + 1;
    byHour[hour] = (byHour[hour] || 0) + 1;
  });
  const days = Object.keys(byDay).length || 1;
  const peak = Object.entries(byHour).sort(([,a],[,b]) => b - a)[0];
  return {
    total:    pomodoros.length,
    avgPerDay: (pomodoros.length / days).toFixed(1),
    mostProductiveHour: peak ? `${peak[0]}:00` : 'N/A',
    totalMinutes: pomodoros.length * 25,
    dailyData: Object.entries(byDay).sort(([a],[b]) => new Date(a)-new Date(b)).map(([date,count]) => ({ date: new Date(date).toLocaleDateString('en-US',{month:'short',day:'numeric'}), sessions: count })),
    hourlyData: Array.from({length:24},(_,i) => ({ hour:i, sessions: byHour[i]||0, label:`${i}:00` })).filter(h => h.sessions > 0),
  };
};

const calcHabitStats = (habits, completions, startDate) => {
  if (!habits.length) return { completionRate: 0, streaks: [], performanceData: [] };

  const totalPossible  = habits.length * getDaysSince(startDate);
  const totalCompleted = Object.entries(completions).filter(([k, v]) => v && new Date(k.split('_')[0]) >= startDate).length;
  const completionRate = totalPossible > 0 ? ((totalCompleted / totalPossible) * 100).toFixed(1) : 0;

  const streaks = habits.map(habit => {
    let current = 0, max = 0, temp = 0, broken = false;
    for (let i = 0; i < 365; i++) {
      const d = new Date(); d.setDate(d.getDate() - i); d.setHours(0,0,0,0);
      const key = `${d.toDateString()}_${habit.id}`;
      if (completions[key]) { temp++; max = Math.max(max, temp); if (!broken) current = temp; }
      else { if (!broken) broken = true; temp = 0; }
    }
    return { name: habit.name, currentStreak: current, maxStreak: Math.max(max, current) };
  });

  const daysSince = getDaysSince(startDate);
  const performanceData = habits.map(habit => {
    const done = Object.entries(completions).filter(([k, v]) => k.includes(`_${habit.id}`) && v && new Date(k.split('_')[0]) >= startDate).length;
    return { name: habit.name, completions: done, rate: daysSince > 0 ? ((done / daysSince) * 100).toFixed(0) : 0 };
  });

  return { completionRate, streaks, performanceData };
};

const calcWorkoutStats = (workouts) => {
  if (!workouts.length) return { total: 0, totalVolume: 0, avgDuration: 0, frequencyData: [], totalMinutes: 0 };
  const totalVolume   = workouts.reduce((s, w) => s + w.exerciseData.reduce((es, ex) => es + ex.sets.reduce((ss, set) => ss + (set.reps||0)*(set.weight||0), 0), 0), 0);
  const totalDuration = workouts.reduce((s, w) => s + (w.duration || 0), 0);
  const byDay = {};
  workouts.forEach(w => { const d = new Date(w.startTime).toLocaleDateString(); byDay[d] = (byDay[d]||0)+1; });
  return {
    total: workouts.length,
    totalVolume,
    avgDuration: Math.round(totalDuration / workouts.length / 60),
    totalMinutes: Math.round(totalDuration / 60),
    frequencyData: Object.entries(byDay).sort(([a],[b]) => new Date(a)-new Date(b)).map(([date,count]) => ({ date: new Date(date).toLocaleDateString('en-US',{month:'short',day:'numeric'}), workouts: count })),
  };
};

const calcAssignmentStats = (assignments) => {
  if (!assignments.length) return { total: 0, completed: 0, onTimeRate: 0, upcoming: 0 };
  const completed = assignments.filter(a => a.status === 'completed').length;
  const today = new Date(); today.setHours(0,0,0,0);
  return {
    total: assignments.length,
    completed,
    onTimeRate: completed > 0 ? ((completed / completed) * 100).toFixed(0) : 0,
    upcoming: assignments.filter(a => a.date && new Date(a.date) >= today && a.status !== 'completed').length,
  };
};

const calcFinanceStats = (transactions) => {
  const income   = transactions.filter(t => t.type==='income').reduce((s,t) => s+parseFloat(t.amount), 0);
  const expenses = transactions.filter(t => t.type==='expense').reduce((s,t) => s+parseFloat(t.amount), 0);
  const byCategory = {};
  transactions.filter(t => t.type==='expense').forEach(t => { byCategory[t.category] = (byCategory[t.category]||0)+parseFloat(t.amount); });
  return {
    income, expenses,
    balance: income - expenses,
    categoryData: Object.entries(byCategory).map(([cat, amount]) => ({ name: cat.charAt(0).toUpperCase()+cat.slice(1), value: amount })),
  };
};

export default Statistics;